import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import "./newLandingPreview.css";
import logoOrange from "./assets/Oransje_Logo.svg";
import symbolOnDark from "./assets/symbol-on-dark.svg";
import { fetchCities, fetchLandingConfig, countLocationsNear } from "./cities";
import { newSessionToken, getCityPredictions, getPlaceLocation } from "./googlePlaces";
import { buildOversiktPath } from "./seo";
import { useIsMobile } from "./useIsMobile";

/*
 * Hidden preview of the redesigned Mapli landing page.
 * Not linked from anywhere in the live site — reachable only by visiting
 * /new-landing-preview directly, so it can be iterated on before launch.
 *
 * Two-tier city search:
 * - Tier 1: real `cities` docs from Firestore. Counts are fetched live via
 *   GeoFirestore near() (same pattern as getLocationsAdmin.js) only for the
 *   handful of cities actually visible at once, and cached in `counts`.
 * - Tier 2: Google Places Autocomplete, used only as a fallback when Tier 1
 *   has few/no matches, so a real-world city Mapli hasn't touched yet still
 *   shows up as "coming soon" instead of "no matches".
 */

const COUNT_BUCKET_THRESHOLD = 50;
const QUICK_CITY_COUNT = 5;
const GRID_CITY_COUNT = 12;
const PLACES_FALLBACK_MIN_MATCHES = 5;

const COPY = {
  no: {
    navCities: "Byer",
    navApp: "Last ned appen",
    navBusiness: "For bedrifter",
    heroTitle: "Hva skjer rundt deg akkurat nå?",
    heroSub:
      "Mapli samler restauranter, aktiviteter og arrangementer i én liste. Velg en by, så viser vi deg det som er nærmest.",
    searchPlaceholder: "Søk etter en by",
    quickLabel: "Eller hopp rett til:",
    noMatches: "Vi er ikke i den byen ennå. Prøv en annen!",
    loadingCities: "Laster byer …",
    otherCitiesLabel: "Andre byer i verden",
    pickedCta: "Se alt →",
    placesUnit: "steder",
    comingSoon: "Kommer snart til Mapli",
    citiesTitle: "Byer på Mapli",
    citiesSub: "Et utvalg av byer fra hele verden. Søk etter din!",
    moreCities: "+ Mange flere byer",
    moreCitiesCta: "Søk etter din by",
    catRest: "Restauranter",
    catRestBody: "Meny, prisnivå, åpningstid og omtaler fra Tripadvisor, Google og Yelp samlet på ett sted.",
    catRestCta: "Finn et sted å spise →",
    catAct: "Aktiviteter",
    catActBody: "Museer, turer, klatring, bad. Alt du kan finne på i dag, sortert etter hvor nærme det er.",
    catActCta: "Se hva du kan gjøre →",
    catEv: "Arrangementer",
    catEvBody: "Konserter, marked og festival — med dato, sted og veibeskrivelse.",
    catEvCta: "Se hva som skjer →",
    step1: "Velg by",
    step1Body: "Søk, eller trykk på en av byene over.",
    step2: "Filtrer",
    step2Body: "Søk i menyene etter burger, rødvin eller veganske retter, og sett prisnivå og avstand.",
    step3: "Dra av gårde",
    step3Body: "Åpne stedet, se omtalene og få veibeskrivelse.",
    appTitle: "Ta Mapli med ut",
    appBody: "Kartet, filtrene og listen — i lomma.",
    footer: "© 2026 Mapli",
    footerPrivacy: "Personvern",
    footerContact: "Kontakt",
  },
  en: {
    navCities: "Cities",
    navApp: "Get the app",
    navBusiness: "For business",
    heroTitle: "What is happening around you right now?",
    heroSub:
      "Mapli gathers restaurants, activities and events in one list. Pick a city and we show you what is closest.",
    searchPlaceholder: "Search for a city",
    quickLabel: "Or jump straight to:",
    noMatches: "We are not in that city yet. Try another one!",
    loadingCities: "Loading cities …",
    otherCitiesLabel: "Other cities worldwide",
    pickedCta: "See everything →",
    placesUnit: "places",
    comingSoon: "Coming soon to Mapli",
    citiesTitle: "Cities on Mapli",
    citiesSub: "A selection of cities from around the world. Search for yours!",
    moreCities: "+ Many more cities",
    moreCitiesCta: "Search for your city",
    catRest: "Restaurants",
    catRestBody: "Menu, price tier, opening hours and reviews from Tripadvisor, Google and Yelp in one place.",
    catRestCta: "Find a place to eat →",
    catAct: "Activities",
    catActBody: "Museums, hikes, climbing, swimming. Everything to do today, sorted by how close it is.",
    catActCta: "See what you can do →",
    catEv: "Events",
    catEvBody: "Concerts, markets and festivals — with date, place and directions.",
    catEvCta: "See what is on →",
    step1: "Pick a city",
    step1Body: "Search, or tap one of the cities above.",
    step2: "Filter",
    step2Body: "Search menus for burgers, red wine or vegan dishes, and set price tier and distance.",
    step3: "Head out",
    step3Body: "Open the place, read the reviews and get directions.",
    appTitle: "Take Mapli with you",
    appBody: "The map, the filters and the list — in your pocket.",
    footer: "© 2026 Mapli",
    footerPrivacy: "Privacy",
    footerContact: "Contact",
  },
};

// Deep green stands in for the light activities token here — the app's
// --greenActivityColor is too pale for white text on a solid card fill.
const GREEN_DEEP = "#2c6b4c";
const CAT_COLORS = ["var(--purpleRestaurantColor)", GREEN_DEEP, "var(--blueEventsColor)"];

const CATEGORY_CARDS = [
  { cat: "restaurants", titleKey: "catRest", bodyKey: "catRestBody", ctaKey: "catRestCta", bg: CAT_COLORS[0] },
  { cat: "activities", titleKey: "catAct", bodyKey: "catActBody", ctaKey: "catActCta", bg: CAT_COLORS[1] },
  { cat: "events", titleKey: "catEv", bodyKey: "catEvBody", ctaKey: "catEvCta", bg: CAT_COLORS[2] },
];

const STEP_KEYS = [
  { n: "01", titleKey: "step1", bodyKey: "step1Body" },
  { n: "02", titleKey: "step2", bodyKey: "step2Body" },
  { n: "03", titleKey: "step3", bodyKey: "step3Body" },
];

function Icon({ name, size = 24, color = "currentColor", style }) {
  return (
    <span
      className="mapli-icon"
      aria-hidden="true"
      style={{ fontSize: size, width: size, height: size, color, ...style }}>
      {name}
    </span>
  );
}

const NewLandingPreview = () => {
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const [lang, setLang] = useState("no");
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [picked, setPicked] = useState(null); // { tier: "known", name, city } | { tier: "unknown", name }

  const [cities, setCities] = useState([]);
  const [citiesLoading, setCitiesLoading] = useState(true);
  const [landingConfig, setLandingConfig] = useState({ quickCities: [], featuredCities: [] });
  const [counts, setCounts] = useState({}); // cityId -> number
  const pendingCounts = useRef(new Set());

  const [placeSuggestions, setPlaceSuggestions] = useState([]);
  const sessionTokenRef = useRef(null);

  const inputRef = useRef(null);
  const searchRef = useRef(null);

  const t = COPY[lang];
  const localeNumber = (n) => n.toLocaleString(lang === "no" ? "nb-NO" : "en-US");

  useEffect(() => {
    fetchCities()
      .then(setCities)
      .catch(() => setCities([]))
      .finally(() => setCitiesLoading(false));
    fetchLandingConfig()
      .then(setLandingConfig)
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!open) return;
    const onOutsideClick = (e) => {
      if (searchRef.current && !searchRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onOutsideClick);
    return () => document.removeEventListener("mousedown", onOutsideClick);
  }, [open]);

  // Fetch (and cache) a live location count for one city, only once.
  const ensureCount = useCallback((city) => {
    if (!city?.id || counts[city.id] !== undefined || pendingCounts.current.has(city.id)) return;
    pendingCounts.current.add(city.id);
    countLocationsNear(city)
      .then((n) => setCounts((prev) => ({ ...prev, [city.id]: n })))
      .catch(() => setCounts((prev) => ({ ...prev, [city.id]: 0 })))
      .finally(() => pendingCounts.current.delete(city.id));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [counts]);

  const sortedCities = useMemo(() => [...cities].sort((a, b) => a.name.localeCompare(b.name)), [cities]);
  const cityByName = useMemo(() => {
    const map = new Map();
    cities.forEach((c) => map.set(c.name.toLowerCase(), c));
    return map;
  }, [cities]);

  // Curated order from siteConfig/landingPage when set; falls back to the
  // first N cities alphabetically so the page still works before it's set up.
  // A curated name only resolves once a matching `cities` doc exists (its
  // lat/lng is what makes the pick/count logic work) — anything that
  // doesn't match yet is logged instead of failing silently.
  const resolveCuratedList = useCallback(
    (names, fallback, label) => {
      if (!citiesLoading && names.length > 0) {
        const missing = names.filter((n) => !cityByName.has(n.toLowerCase()));
        if (missing.length > 0) {
          console.warn(
            `[NewLandingPreview] siteConfig/landingPage.${label} has ${missing.length} name(s) with no matching cities doc yet:`,
            missing,
          );
        }
      }
      const resolved = names.map((n) => cityByName.get(n.toLowerCase())).filter(Boolean);
      return resolved.length > 0 ? resolved : fallback;
    },
    [cityByName, citiesLoading],
  );

  const quickCities = useMemo(
    () => resolveCuratedList(landingConfig.quickCities, sortedCities.slice(0, QUICK_CITY_COUNT), "quickCities"),
    [landingConfig.quickCities, sortedCities, resolveCuratedList],
  );
  const gridCities = useMemo(
    () => resolveCuratedList(landingConfig.featuredCities, sortedCities.slice(0, GRID_CITY_COUNT), "featuredCities"),
    [landingConfig.featuredCities, sortedCities, resolveCuratedList],
  );

  const matches = useMemo(() => {
    const q = query.trim().toLowerCase();
    return q ? cities.filter((c) => c.name.toLowerCase().includes(q)).slice(0, 8) : cities.slice(0, 6);
  }, [query, cities]);

  useEffect(() => { quickCities.forEach(ensureCount); }, [quickCities, ensureCount]);
  useEffect(() => { gridCities.forEach(ensureCount); }, [gridCities, ensureCount]);
  useEffect(() => { matches.forEach(ensureCount); }, [matches, ensureCount]);
  useEffect(() => {
    if (picked?.tier === "known") ensureCount(picked.city);
  }, [picked, ensureCount]);

  // Tier 2: only bother calling Places when Tier 1 came back thin.
  useEffect(() => {
    const q = query.trim();
    if (q.length < 2 || matches.length >= PLACES_FALLBACK_MIN_MATCHES) {
      setPlaceSuggestions([]);
      return;
    }

    let cancelled = false;
    const timer = setTimeout(async () => {
      try {
        if (!sessionTokenRef.current) sessionTokenRef.current = await newSessionToken();
        const predictions = await getCityPredictions({ input: q, sessionToken: sessionTokenRef.current, language: lang });
        if (cancelled) return;
        const known = new Set(matches.map((c) => c.name.toLowerCase()));
        setPlaceSuggestions(predictions.filter((p) => !known.has(p.name.toLowerCase())).slice(0, 5));
      } catch {
        if (!cancelled) setPlaceSuggestions([]);
      }
    }, 300);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [query, matches, lang]);

  // { text, empty, pending } — empty/pending callers fall back to comingSoon-style italics.
  const labelFromCount = (n) => {
    if (n === undefined) return { text: "…", pending: true };
    if (n === 0) return { text: t.comingSoon, empty: true };
    if (n < COUNT_BUCKET_THRESHOLD) {
      const bucket = Math.floor(n / 10) * 10 || n;
      return { text: `${localeNumber(bucket)}+ ${t.placesUnit}` };
    }
    return { text: `${localeNumber(n)} ${t.placesUnit}` };
  };
  const countLabel = (city) => labelFromCount(counts[city.id]);

  const pickCity = (city) => {
    setPicked({ tier: "known", name: city.name, city });
    setQuery(city.name);
    setOpen(false);
  };

  // Quick chips and search suggestions still go through the pick-then-
  // confirm banner (useful when you're not sure a city has anything yet).
  // The city grid is already a curated "these are real, browse them"
  // section, so a real (non-empty) city there skips straight to results —
  // one fewer click. A "coming soon" city still just shows the banner,
  // since navigating to an empty results page isn't useful.
  const goToCityDirect = (city) => {
    if (countLabel(city).empty) {
      pickCity(city);
      return;
    }
    const prettyPath = buildOversiktPath({ lang, city, category: "restaurants" });
    navigate(prettyPath || `/new-oversikt-preview?city=${city.id}&category=restaurants`);
  };

  // Tier 2 has no Firestore doc to trust, so we don't know yet whether this
  // is a real gap or just a city nobody's added to `cities` — geocode the
  // pick and run the same near() check against real inventory before
  // deciding it's "coming soon".
  const pickPlace = async (place) => {
    setPicked({ tier: "unknown", name: place.name, resolving: true });
    setQuery(place.name);
    setOpen(false);
    const token = sessionTokenRef.current;
    sessionTokenRef.current = null; // billing: reset once a selection completes

    try {
      const location = await getPlaceLocation(place.placeId, token);
      if (!location) {
        setPicked({ tier: "unknown", name: place.name });
        return;
      }
      const n = await countLocationsNear(location);
      setPicked({ tier: "resolved", name: place.name, count: n, location });
    } catch {
      setPicked({ tier: "unknown", name: place.name });
    }
  };

  const clearQuery = () => {
    setQuery("");
    setPicked(null);
    setOpen(false);
    setPlaceSuggestions([]);
  };

  const focusSearch = () => {
    setOpen(true);
    window.scrollTo({ top: 0, behavior: "smooth" });
    inputRef.current && inputRef.current.focus();
  };

  const pickedLabel = !picked
    ? null
    : picked.tier === "known"
      ? countLabel(picked.city)
      : picked.tier === "resolved"
        ? labelFromCount(picked.count)
        : picked.resolving
          ? { text: "…", pending: true }
          : { text: t.comingSoon, empty: true }; // couldn't geocode it at all — genuinely unknown
  const showPickedCta = picked && picked.tier !== "unknown" && !pickedLabel?.empty && !pickedLabel?.pending;

  const goToOversiktCategory = (category) => {
    if (!picked) return;
    if (picked.tier === "known") {
      // Real `cities` doc with a known country -> pretty, SEO-shaped URL.
      const prettyPath = buildOversiktPath({ lang, city: picked.city, category });
      if (prettyPath) {
        navigate(prettyPath);
        return;
      }
      navigate(`/new-oversikt-preview?city=${picked.city.id}&category=${category}`);
      return;
    }
    // Tier 2 (Places fallback, no `cities` doc / no countryCode yet) can't
    // build a pretty path — fall back to the plain query-param route.
    const params = new URLSearchParams({ category, name: picked.name, lat: picked.location.lat, lng: picked.location.lng });
    navigate(`/new-oversikt-preview?${params.toString()}`);
  };

  const goToOversikt = () => goToOversiktCategory("restaurants");

  // Category cards: jump straight to that category for the picked city, or
  // prompt for a city first if none is picked yet (an empty results page
  // isn't useful).
  const onCategoryCardClick = (category) => {
    if (picked && showPickedCta) {
      goToOversiktCategory(category);
      return;
    }
    focusSearch();
  };

  return (
    <div
      className="mapliV2"
      data-category="restaurants"
      style={{ fontFamily: "var(--font-ui)" }}>
      <header
        style={{
          position: "sticky",
          top: 0,
          zIndex: 40,
          background: "var(--whiteBackgroundColor)",
          borderBottom: "1px solid var(--border-hairline)",
        }}>
        <div
          style={{
            maxWidth: 1180,
            margin: "0 auto",
            padding: isMobile ? "0 16px" : "0 32px",
            height: isMobile ? 58 : 72,
            display: "flex",
            alignItems: "center",
            gap: isMobile ? 12 : 24,
          }}>
          <img src={logoOrange} alt="Mapli" style={{ height: isMobile ? 22 : 26, display: "block", flex: "0 0 auto" }} />
          <nav style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: isMobile ? 12 : 22, flex: "0 0 auto" }}>
            {!isMobile && (
              <>
                <a href="#byer" className="navLink" style={{ font: "var(--type-caption)", color: "var(--ink-700)" }}>
                  {t.navCities}
                </a>
                <a href="#app" className="navLink" style={{ font: "var(--type-caption)", color: "var(--ink-700)" }}>
                  {t.navApp}
                </a>
              </>
            )}
            <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
              <button
                onClick={() => setLang("no")}
                className="langBtn"
                style={{
                  border: "none",
                  background: "transparent",
                  cursor: "pointer",
                  padding: "4px 2px",
                  font: "var(--type-caption)",
                  color: lang === "no" ? "var(--mapli-coral)" : "var(--ink-500)",
                }}>
                NO
              </button>
              <span style={{ color: "var(--ink-300)", font: "var(--type-caption)" }}>/</span>
              <button
                onClick={() => setLang("en")}
                className="langBtn"
                style={{
                  border: "none",
                  background: "transparent",
                  cursor: "pointer",
                  padding: "4px 2px",
                  font: "var(--type-caption)",
                  color: lang === "en" ? "var(--mapli-coral)" : "var(--ink-500)",
                }}>
                EN
              </button>
            </div>
            <a
              href="/login"
              className="businessBtn"
              style={{
                background: "var(--mapli-coral)",
                color: "#fff",
                font: "var(--type-caption)",
                borderRadius: "var(--radius-pill)",
                padding: isMobile ? "6px 12px" : "7px 16px",
                whiteSpace: "nowrap",
              }}>
              {t.navBusiness}
            </a>
          </nav>
        </div>
      </header>

      <section style={{ background: "var(--surface-app)", borderBottom: "1px solid var(--border-hairline)" }}>
        <div
          style={{
            maxWidth: 1180,
            margin: "0 auto",
            padding: isMobile ? "40px 16px 36px" : "72px 32px 64px",
            display: "grid",
            gap: isMobile ? 22 : 34,
            justifyItems: "center",
            textAlign: "center",
          }}>
          <h1
            style={{
              margin: 0,
              font: "var(--type-display)",
              fontSize: "clamp(34px, 8vw, 64px)",
              lineHeight: 1.08,
              color: "var(--ink-900)",
              maxWidth: "16em",
              textWrap: "pretty",
            }}>
            {t.heroTitle}
          </h1>
          <p
            style={{
              margin: 0,
              font: "var(--type-body)",
              fontSize: isMobile ? 16 : 20,
              color: "var(--ink-700)",
              maxWidth: "34em",
              textWrap: "pretty",
            }}>
            {t.heroSub}
          </p>

          <div ref={searchRef} style={{ width: "100%", maxWidth: 620, position: "relative", textAlign: "left" }}>
            <div
              className="searchPill"
              style={{
                display: "flex",
                alignItems: "center",
                gap: isMobile ? 8 : 12,
                background: "var(--whiteBackgroundColor)",
                borderRadius: "var(--radius-pill)",
                boxShadow: "var(--shadow-float)",
                padding: isMobile ? "0 16px" : "0 22px",
                height: isMobile ? 54 : 66,
              }}>
              <Icon name="search" size={isMobile ? 22 : 26} color="var(--mapli-coral)" />
              <input
                ref={inputRef}
                type="text"
                className="searchInput"
                value={query}
                onChange={(e) => {
                  setQuery(e.target.value);
                  setOpen(true);
                }}
                onFocus={() => setOpen(true)}
                placeholder={t.searchPlaceholder}
                style={{
                  flex: 1,
                  minWidth: 0,
                  fontSize: isMobile ? 16 : 20,
                  color: "var(--ink-900)",
                }}
              />
              {query.length > 0 && (
                <button
                  onClick={clearQuery}
                  aria-label="Tøm"
                  className="clearQueryBtn"
                  style={{ border: "none", background: "transparent", padding: 4, cursor: "pointer", display: "flex" }}>
                  <Icon name="close" size={22} color="var(--ink-500)" />
                </button>
              )}
            </div>
            <div
              style={{
                display: open ? "grid" : "none",
                position: "absolute",
                left: 0,
                right: 0,
                top: isMobile ? 62 : 74,
                zIndex: 30,
                background: "var(--whiteBackgroundColor)",
                borderRadius: "var(--radius-panel)",
                boxShadow: "var(--shadow-float)",
                padding: 8,
                maxHeight: 360,
                overflowY: "auto",
              }}>
              {citiesLoading && (
                <span style={{ font: "var(--type-body)", color: "var(--ink-500)", padding: "10px 12px" }}>
                  {t.loadingCities}
                </span>
              )}
              {!citiesLoading &&
                matches.map((c) => {
                  const label = countLabel(c);
                  return (
                    <button
                      key={c.id}
                      onClick={() => pickCity(c)}
                      className="suggestionRow"
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 10,
                        width: "100%",
                        textAlign: "left",
                        border: "none",
                        background: "transparent",
                        cursor: "pointer",
                        padding: "10px 12px",
                        borderRadius: "var(--radius-card)",
                      }}>
                      <Icon name="near_me" size={20} color="var(--ink-300)" />
                      <span style={{ font: "var(--type-body)", color: "var(--ink-900)" }}>{c.name}</span>
                      <span
                        style={{
                          marginLeft: "auto",
                          font: "var(--type-caption)",
                          fontStyle: label.empty || label.pending ? "italic" : "normal",
                          color: "var(--ink-500)",
                        }}>
                        {label.text}
                      </span>
                    </button>
                  );
                })}
              {!citiesLoading && placeSuggestions.length > 0 && (
                <span style={{ font: "var(--type-caption)", color: "var(--ink-300)", padding: "10px 12px 2px" }}>
                  {t.otherCitiesLabel}
                </span>
              )}
              {!citiesLoading &&
                placeSuggestions.map((p) => (
                  <button
                    key={p.placeId}
                    onClick={() => pickPlace(p)}
                    className="suggestionRow"
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 10,
                      width: "100%",
                      textAlign: "left",
                      border: "none",
                      background: "transparent",
                      cursor: "pointer",
                      padding: "10px 12px",
                      borderRadius: "var(--radius-card)",
                    }}>
                    <Icon name="near_me" size={20} color="var(--ink-300)" />
                    <span style={{ font: "var(--type-body)", color: "var(--ink-900)" }}>{p.name}</span>
                    <span style={{ marginLeft: "auto", font: "var(--type-caption)", color: "var(--ink-500)" }}>
                      {p.secondary}
                    </span>
                  </button>
                ))}
              {!citiesLoading && matches.length === 0 && placeSuggestions.length === 0 && (
                <span style={{ font: "var(--type-body)", color: "var(--ink-500)", padding: "10px 12px" }}>
                  {t.noMatches}
                </span>
              )}
            </div>
          </div>

          <div style={{ display: "grid", gap: 12, justifyItems: "center" }}>
            <span style={{ font: "var(--type-caption)", color: "var(--ink-500)" }}>{t.quickLabel}</span>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 10, justifyContent: "center" }}>
              {quickCities.map((c) => (
                <button
                  key={c.id}
                  onClick={() => pickCity(c)}
                  className="quickCity"
                  style={{
                    border: "1px solid var(--border-hairline)",
                    background: picked?.name === c.name ? "var(--mapli-coral)" : "var(--whiteBackgroundColor)",
                    color: picked?.name === c.name ? "#fff" : "var(--ink-900)",
                    borderRadius: "var(--radius-pill)",
                    padding: isMobile ? "8px 16px" : "10px 20px",
                    cursor: "pointer",
                    font: "var(--type-body-strong)",
                    fontSize: isMobile ? 16 : 18,
                    transition: "background var(--dur-fast) var(--ease-standard)",
                  }}>
                  {c.name}
                </button>
              ))}
            </div>
          </div>

          {picked && (
            <div
              style={{
                display: "flex",
                flexWrap: "wrap",
                alignItems: "center",
                justifyContent: "center",
                gap: 16,
                background: "var(--whiteBackgroundColor)",
                borderRadius: "var(--radius-panel)",
                padding: isMobile ? "14px 18px" : "18px 24px",
                boxShadow: "var(--shadow-float)",
              }}>
              <span style={{ font: "var(--type-body-strong)", fontSize: isMobile ? 17 : 20, color: "var(--ink-900)" }}>
                {picked.name}
              </span>
              <span
                style={{
                  font: "var(--type-body)",
                  fontStyle: pickedLabel.empty || pickedLabel.pending ? "italic" : "normal",
                  color: "var(--ink-700)",
                }}>
                {pickedLabel.text}
              </span>
              {showPickedCta && (
                <button
                  onClick={goToOversikt}
                  style={{ border: "none", background: "transparent", padding: 0, cursor: "pointer", font: "var(--type-body-strong)", color: "var(--text-link)" }}>
                  {t.pickedCta}
                </button>
              )}
            </div>
          )}
        </div>
      </section>

      <section style={{ maxWidth: 1180, margin: "0 auto", padding: isMobile ? "36px 16px 8px" : "64px 32px 8px" }}>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))", gap: 18 }}>
          {CATEGORY_CARDS.map((cc) => (
            <button
              key={cc.cat}
              data-category={cc.cat}
              onClick={() => onCategoryCardClick(cc.cat)}
              className="categoryCard"
              style={{
                background: cc.bg,
                borderRadius: "var(--radius-panel)",
                padding: 26,
                display: "grid",
                gap: 10,
                alignContent: "start",
                textAlign: "left",
                border: "none",
                cursor: "pointer",
                transition: "transform var(--dur-fast) var(--ease-standard)",
              }}>
              <h2 style={{ margin: 0, font: "var(--type-card-title)", color: "#fff" }}>{t[cc.titleKey]}</h2>
              <p style={{ margin: 0, font: "var(--type-body)", color: "#fff", textWrap: "pretty" }}>{t[cc.bodyKey]}</p>
              <span style={{ marginTop: 4, font: "var(--type-body-strong)", color: "#fff" }}>{t[cc.ctaKey]}</span>
            </button>
          ))}
        </div>
      </section>

      <section id="byer" style={{ maxWidth: 1180, margin: "0 auto", padding: isMobile ? "36px 16px 8px" : "56px 32px 8px", display: "grid", gap: 20 }}>
        <div style={{ display: "flex", alignItems: "baseline", gap: 14, flexWrap: "wrap" }}>
          <h2 style={{ margin: 0, font: "var(--type-card-title)", fontSize: 28, color: "var(--ink-900)" }}>
            {t.citiesTitle}
          </h2>
          <span style={{ font: "var(--type-body)", color: "var(--ink-500)" }}>{t.citiesSub}</span>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))", gap: 14 }}>
          {citiesLoading && (
            <span style={{ font: "var(--type-body)", color: "var(--ink-500)" }}>{t.loadingCities}</span>
          )}
          {!citiesLoading &&
            gridCities.map((c) => {
              const label = countLabel(c);
              return (
                <button
                  key={c.id}
                  onClick={() => goToCityDirect(c)}
                  className="cityCard"
                  style={{
                    display: "grid",
                    justifyItems: "start",
                    gap: 2,
                    textAlign: "left",
                    background: "var(--surface-app)",
                    border: "none",
                    borderRadius: "var(--radius-panel)",
                    padding: "20px 22px",
                    cursor: "pointer",
                  }}>
                  <span style={{ font: "var(--type-card-title)", fontSize: 21, color: "var(--ink-900)" }}>{c.name}</span>
                  <span
                    style={{
                      marginTop: 6,
                      font: "var(--type-caption)",
                      fontStyle: label.empty || label.pending ? "italic" : "normal",
                      color: "var(--ink-700)",
                    }}>
                    {label.text}
                  </span>
                </button>
              );
            })}
          <button
            onClick={focusSearch}
            className="moreCitiesCard"
            style={{
              display: "grid",
              justifyItems: "start",
              gap: 2,
              textAlign: "left",
              background: "transparent",
              border: "1px dashed var(--ink-150)",
              borderRadius: "var(--radius-panel)",
              padding: "20px 22px",
              cursor: "pointer",
              transition: "border-color var(--dur-fast) var(--ease-standard)",
            }}>
            <span style={{ font: "var(--type-card-title)", fontSize: 21, color: "var(--ink-700)" }}>{t.moreCities}</span>
            <span style={{ marginTop: 6, font: "var(--type-caption)", color: "var(--mapli-coral)" }}>
              {t.moreCitiesCta}
            </span>
          </button>
        </div>
      </section>

      <section style={{ maxWidth: 1180, margin: "0 auto", padding: isMobile ? "36px 16px 0" : "56px 32px 0" }}>
        <div
          style={{
            background: "var(--surface-app)",
            borderRadius: "var(--radius-panel)",
            padding: isMobile ? 22 : 34,
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
            gap: isMobile ? 20 : 28,
          }}>
          {STEP_KEYS.map((st, i) => (
            <div key={st.n} style={{ display: "grid", gap: 8, alignContent: "start" }}>
              <span
                style={{
                  font: "var(--type-body-strong)",
                  color: "#fff",
                  background: CAT_COLORS[i],
                  borderRadius: 999,
                  width: 32,
                  height: 32,
                  display: "grid",
                  placeItems: "center",
                }}>
                {st.n}
              </span>
              <span style={{ font: "var(--type-body-strong)", fontSize: 20, color: "var(--ink-900)" }}>
                {t[st.titleKey]}
              </span>
              <span style={{ font: "var(--type-body)", color: "var(--ink-700)", textWrap: "pretty" }}>
                {t[st.bodyKey]}
              </span>
            </div>
          ))}
        </div>
      </section>

      <section id="app" style={{ maxWidth: 1180, margin: "0 auto", padding: isMobile ? "36px 16px 48px" : "56px 32px 64px" }}>
        <div
          style={{
            background: "var(--mapli-coral)",
            borderRadius: "var(--radius-panel)",
            padding: isMobile ? 24 : 38,
            display: "flex",
            flexWrap: "wrap",
            alignItems: "center",
            gap: isMobile ? 18 : 26,
          }}>
          <img src={symbolOnDark} alt="" style={{ height: isMobile ? 44 : 56, display: "block", flex: "0 0 auto" }} />
          <div style={{ minWidth: 240, flex: 1, display: "grid", gap: 6 }}>
            <span style={{ font: "var(--type-card-title)", fontSize: 26, color: "#fff" }}>{t.appTitle}</span>
            <span style={{ font: "var(--type-body)", color: "#fff" }}>{t.appBody}</span>
          </div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 12 }}>
            <a
              href="https://play.google.com/store/apps/details?id=com.mapli"
              className="storeBtn"
              style={{
                background: "#fff",
                color: "var(--ink-900)",
                borderRadius: "var(--radius-pill)",
                padding: "12px 22px",
                font: "var(--type-body-strong)",
              }}>
              Google Play
            </a>
            <a
              href="https://apps.apple.com/us/app/mapli/id6748616006"
              className="storeBtn"
              style={{
                background: "#fff",
                color: "var(--ink-900)",
                borderRadius: "var(--radius-pill)",
                padding: "12px 22px",
                font: "var(--type-body-strong)",
              }}>
              App Store
            </a>
          </div>
        </div>
      </section>

      <footer style={{ borderTop: "1px solid var(--border-hairline)" }}>
        <div
          style={{
            maxWidth: 1180,
            margin: "0 auto",
            padding: isMobile ? "20px 16px" : "26px 32px",
            display: "flex",
            flexWrap: "wrap",
            gap: 18,
            alignItems: "center",
          }}>
          <img src={logoOrange} alt="Mapli" style={{ height: 20, display: "block", opacity: 0.8 }} />
          <span style={{ font: "var(--type-caption)", color: "var(--ink-500)" }}>{t.footer}</span>
          <div style={{ marginLeft: "auto", display: "flex", gap: 18 }}>
            <a href="/privacy" style={{ font: "var(--type-caption)", color: "var(--ink-700)" }}>
              {t.footerPrivacy}
            </a>
            <a href="/faq" style={{ font: "var(--type-caption)", color: "var(--ink-700)" }}>
              {t.footerContact}
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default NewLandingPreview;
